import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  getEntityLocationName,
  LOCATION_NAME_REQUIRED_MESSAGE,
} from '@/lib/festival-location';
import {
  FileUpload,
  FormButtons,
  FormRow,
  LocationMapField,
  TagsInput,
} from '../controls';

function buildDefaultValues(service) {
  return {
    serviceName: service.serviceName || '',
    serviceDescription: service.serviceDescription || '',
    type: service.type || '',
  };
}

export function ServiceForm({
  service,
  types,
  handleSave,
  handleLocationChange,
  handleLocationNameChange,
  onFileUpload,
  onFileDelete,
  handleRemove,
}) {
  const [locationNameError, setLocationNameError] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues(service),
    mode: 'onSubmit',
  });

  const serviceId = service?.serviceId;

  useEffect(() => {
    reset(buildDefaultValues(service));
  }, [serviceId, reset]);

  const fileList = [];

  if (service?.photoURL) {
    fileList.push({
      uid: '-1',
      name: service.servicePhoto,
      url: service.photoURL,
      status: 'done',
    });
  }

  const submit = (values) => {
    if (!getEntityLocationName(service)) {
      setLocationNameError(LOCATION_NAME_REQUIRED_MESSAGE);
      return;
    }
    setLocationNameError(null);
    handleSave(values);
  };

  const onLocationChange = (location, displayName) => {
    if (displayName?.trim()) {
      setLocationNameError(null);
    }
    handleLocationChange?.(location, displayName);
  };

  const onLocationNameChange = (nextLocationName) => {
    if (nextLocationName?.trim()) {
      setLocationNameError(null);
    }
    handleLocationNameChange?.(nextLocationName);
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="flex">
        <div className="w-3/4 p-4">
          <FormRow label="Service Name" error={errors.serviceName?.message}>
            <input
              type="text"
              placeholder="Name"
              {...register('serviceName', {
                required: 'Service name is required',
              })}
            />
          </FormRow>
          <FormRow label="Type" error={errors.type?.message}>
            <Controller
              name="type"
              control={control}
              rules={{ required: 'Service type is required' }}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a type"
                  options={
                    Array.isArray(types) ? types : Object.values(types ?? {})
                  }
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
        </div>
        <div className="w-1/4 p-4">
          <div className="w-full">
            <FileUpload
              fileList={fileList}
              onFileUpload={onFileUpload}
              onFileDelete={onFileDelete}
            />
          </div>
        </div>
      </div>
      <FormRow label="Description">
        <textarea
          placeholder="Service Description..."
          {...register('serviceDescription')}
        />
      </FormRow>
      <FormRow error={locationNameError}>
        <LocationMapField
          key={serviceId}
          shouldPromptForMap
          location={service.location}
          locationName={service.location?.locationName}
          onLocationChange={onLocationChange}
          onLocationNameChange={onLocationNameChange}
        />
      </FormRow>
      <FormButtons
        showDelete={service.serviceId}
        title="Service"
        onDelete={handleRemove}
      />
    </form>
  );
}
